"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Check } from "lucide-react";
const tiers = [{
  id: 1,
  title: "Essential",
  price: "Starting at $800",
  period: "per event",
  features: ["4 hours of DJ service", "2 Speaker PA sound System", "Wireless Microphones"]
}, {
  id: 2,
  title: "Premium",
  price: "Starting at $1,000",
  period: "per event",
  features: ["Everything in Essential", "Dual 18 Inch Subwoofers", "Basic Lighting", "In depth music consultation", "Custom curated playlists", "Additional PA Speakers"]
}, {
  id: 3,
  title: "Ultimate",
  price: "Starting at $1,200",
  period: "per event",
  features: ["8 hours of DJ services", "Top-tier sound system", "Full lighting production", "Dance floor effects", "Sound reactive lighting", "Moving head lighting"]
}];
export default function PricingTiers() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.15,
    once: true
  });
  return <section id="pricing-tiers" ref={sectionRef} className="py-16 md:py-24 bg-light-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
            Choose your perfect DJ package
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Select the ideal DJ and AV package for your special day in Denver
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {tiers.map((tier, i) => <Card key={tier.id} data-index={i} className={`
                card shadow-soft hover:shadow-medium hover:-translate-y-0.5 
                transition-all duration-[var(--duration-medium)] 
                ${sectionVisible ? "animate-fade-in-up" : "opacity-0"}
                ${i === 1 ? "animate-fade-in-up-delay-100" : ""}
                ${i === 2 ? "animate-fade-in-up-delay-200" : ""}
              `} style={{
          animationDelay: i === 0 ? "0ms" : i === 1 ? "100ms" : "200ms"
        }}>
              <CardHeader className="border-b border-border">
                <CardTitle className="text-2xl font-display text-foreground">
                  {tier.title}
                </CardTitle>
                <div className="mt-4">
                  <Badge variant="secondary" className="text-base px-3 py-1.5 bg-accent text-foreground font-bold">
                    {tier.price}
                  </Badge>
                  <p className="text-sm text-muted-foreground mt-2">{tier.period}</p>
                  
                  {tier.title === "Essential" && <p className="text-xs text-primary-dark font-medium mt-3 bg-primary-light px-3 py-2 rounded-md border border-primary/20">
                      best for small events
                    </p>}

                  {tier.title === "Premium" && <p className="text-xs text-primary-dark font-medium mt-3 bg-primary-light px-3 py-2 rounded-md border border-primary/20">
                      Required for events with over 50 attendees
                    </p>}

                  {tier.title === "Ultimate" && <p className="text-xs text-primary-dark font-medium mt-3 bg-primary-light px-3 py-2 rounded-md border border-primary/20">
                      best for large events
                    </p>}
                </div>

                <div className="h-px bg-gradient-to-r from-transparent via-primary to-transparent mt-4 opacity-0 animate-sweep" style={{
              animation: sectionVisible ? "sweep 0.6s ease-out forwards" : "none",
              animationDelay: i === 0 ? "0.3s" : i === 1 ? "0.4s" : "0.5s"
            }} />
              </CardHeader>

              <CardContent className="pt-6">
                <ul className="space-y-3">
                  {tier.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3 text-sm text-foreground">
                      <Check className="size-5 text-primary-dark shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>)}
                </ul>
              </CardContent>

              <CardFooter className="border-t border-border pt-6 mt-auto">
                <Button asChild className="w-full btn-primary bg-accent text-foreground hover:bg-accent-hover hover:shadow-medium hover:-translate-y-0.5 active:bg-accent-active active:translate-y-0 transition-all duration-[var(--duration-medium)]">
                  <Link to="https://forms.gle/6njDwnynrkRpiDs86" newTab>
                    Get Started
                  </Link>
                </Button>
              </CardFooter>
            </Card>)}
        </div>
      </div>

      <style jsx>{`
        @keyframes sweep {
          0% {
            opacity: 0;
            transform: translateX(-100%);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @media (prefers-reduced-motion: reduce) {
          .animate-fade-in-up,
          .animate-fade-in-up-delay-100,
          .animate-fade-in-up-delay-200 {
            animation: none !important;
            opacity: 1 !important;
          }
          .animate-sweep {
            animation: none !important;
            opacity: 1 !important;
          }
        }
      `}</style>
    </section>;
}